import { httpsCallable } from "firebase/functions";

import { auth, functions } from "@/firebase/authorityClient";
import { isOrganisationAuthorityCommandsFeatureEnabled } from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const SCHEMA_VERSION = 1;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const OPAQUE_REFERENCE_PATTERN = /^[a-f0-9]{64}$/;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;
const INVITE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43,128}$/;
const ASSET_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const CAPABILITY_KEYS = Object.freeze([
  "organisationAdministration",
  "membershipAdministration",
  "roleAdministration",
  "appearanceAdministration",
  "moderateItemPlacements",
  "walletAdministration",
  "monetaryAdministration",
  "siblingRelationshipAdministration",
  "apiKeyAdministration",
]);
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;

export class OrganisationCommandAuthorityServiceError extends Error {
  constructor(
    code = "unknown",
    message = "The organisation could not be updated."
  ) {
    super(message);
    this.name = "OrganisationCommandAuthorityServiceError";
    this.code = code;
  }
}

function fail(code, message) {
  throw new OrganisationCommandAuthorityServiceError(code, message);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function requireExactKeys(value, expectedKeys, code = "invalid-argument") {
  if (!isPlainRecord(value)) fail(code);
  const actual = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const expected = [...expectedKeys].sort();
  const strings = actual.filter((key) => typeof key === "string").sort();
  if (
    actual.some(
      (key) =>
        typeof key !== "string" ||
        descriptors[key]?.enumerable !== true ||
        !Object.prototype.hasOwnProperty.call(descriptors[key], "value")
    ) ||
    strings.length !== expected.length ||
    strings.some((key, index) => key !== expected[index])
  ) {
    fail(code);
  }
  return value;
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument", "The organisation operation is invalid.");
  }
  return value;
}

function normalizeOrgId(value, code = "invalid-argument") {
  if (typeof value !== "string" || !ORG_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeRevision(value, code = "invalid-argument") {
  if (!Number.isSafeInteger(value) || value < 1) fail(code);
  return value;
}

function normalizeCreateRevision(value, code = "invalid-argument") {
  if (value !== 0) fail(code);
  return 0;
}

function normalizeOpaqueReference(value, code = "invalid-argument") {
  if (typeof value !== "string" || !OPAQUE_REFERENCE_PATTERN.test(value)) {
    fail(code);
  }
  return value;
}

function normalizePublicIdentifier(value, code = "invalid-response") {
  if (typeof value !== "string" || !PUBLIC_IDENTIFIER_PATTERN.test(value)) {
    fail(code);
  }
  return value;
}

function normalizeText(
  value,
  { minimum = 0, maximum, nullable = false, code = "invalid-argument" }
) {
  if (nullable && value === null) return null;
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    value.length < minimum ||
    value.length > maximum ||
    CONTROL_CHARACTERS.test(value)
  ) {
    fail(code);
  }
  return value;
}

function normalizeSettings(value) {
  requireExactKeys(value, [
    "name",
    "description",
    "category",
    "avatarAssetId",
    "visibility",
    "economy",
    "joinPolicy",
  ]);
  requireExactKeys(value.economy, ["open"]);
  if (
    !["private", "unlisted", "public"].includes(value.visibility) ||
    !["invite-only", "open"].includes(value.joinPolicy) ||
    typeof value.economy.open !== "boolean" ||
    (value.visibility === "private" && value.joinPolicy === "open") ||
    (value.visibility === "private" && value.economy.open) ||
    (value.avatarAssetId !== null &&
      (typeof value.avatarAssetId !== "string" ||
        !ASSET_ID_PATTERN.test(value.avatarAssetId)))
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    name: normalizeText(value.name, { minimum: 2, maximum: 80 }),
    description: normalizeText(value.description, {
      maximum: 500,
      nullable: true,
    }),
    category: normalizeText(value.category, {
      maximum: 80,
      nullable: true,
    }),
    avatarAssetId: value.avatarAssetId,
    visibility: value.visibility,
    economy: Object.freeze({ open: value.economy.open }),
    joinPolicy: value.joinPolicy,
  });
}

function normalizeCapabilities(value) {
  requireExactKeys(value, CAPABILITY_KEYS);
  if (CAPABILITY_KEYS.some((key) => typeof value[key] !== "boolean")) {
    fail("invalid-argument");
  }
  return Object.freeze(
    Object.fromEntries(CAPABILITY_KEYS.map((key) => [key, value[key]]))
  );
}

function normalizeRole(value) {
  requireExactKeys(value, [
    "title",
    "description",
    "definitionVisibility",
    "holderDisclosurePolicy",
    "maxHolders",
    "capabilities",
  ]);
  if (
    !["public", "members", "administrators"].includes(
      value.definitionVisibility
    ) ||
    !["transparent", "member-choice", "confidential"].includes(
      value.holderDisclosurePolicy
    ) ||
    !Number.isSafeInteger(value.maxHolders) ||
    value.maxHolders < 1 ||
    value.maxHolders > 25
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    title: normalizeText(value.title, { minimum: 1, maximum: 100 }),
    description: normalizeText(value.description, {
      maximum: 500,
      nullable: true,
    }),
    definitionVisibility: value.definitionVisibility,
    holderDisclosurePolicy: value.holderDisclosurePolicy,
    maxHolders: value.maxHolders,
    capabilities: normalizeCapabilities(value.capabilities),
  });
}

export function normalizeUpdateOrganisationIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "orgId",
    "settings",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
    settings: normalizeSettings(value.settings),
  });
}

export function normalizeJoinOrganisationIntent(value) {
  requireExactKeys(value, ["operationId", "expectedRevision", "orgId"]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
  });
}

export function normalizeOrganisationRevisionIntent(value) {
  requireExactKeys(value, ["operationId", "expectedRevision", "orgId"]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
  });
}

export function normalizeCreateRoleIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "orgId",
    "role",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeCreateRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
    role: normalizeRole(value.role),
  });
}

export function normalizeUpdateRoleIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "orgId",
    "roleId",
    "role",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
    roleId: normalizeOrgId(value.roleId),
    role: normalizeRole(value.role),
  });
}

export function normalizeRoleRevisionIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "orgId",
    "roleId",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
    roleId: normalizeOrgId(value.roleId),
  });
}

export function normalizeMembershipRevisionIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "orgId",
    "membershipRef",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
    membershipRef: normalizeOpaqueReference(value.membershipRef),
  });
}

export function normalizeMembershipAssignmentIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "orgId",
    "membershipRef",
    "roleId",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
    membershipRef: normalizeOpaqueReference(value.membershipRef),
    roleId: normalizeOrgId(value.roleId),
  });
}

export function normalizeAcceptRoleAssignmentIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "assignmentRef",
    "acceptedDisclosureEpoch",
  ]);
  if (
    !Number.isSafeInteger(value.acceptedDisclosureEpoch) ||
    value.acceptedDisclosureEpoch < 1
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    assignmentRef: normalizeOpaqueReference(value.assignmentRef),
    acceptedDisclosureEpoch: value.acceptedDisclosureEpoch,
  });
}

export function normalizeCreateInviteIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "orgId",
    "roleIds",
    "expiresAt",
  ]);
  if (
    !Array.isArray(value.roleIds) ||
    value.roleIds.length > 10 ||
    value.roleIds.some((roleId) => {
      try {
        normalizeOrgId(roleId);
        return false;
      } catch (_error) {
        return true;
      }
    }) ||
    new Set(value.roleIds).size !== value.roleIds.length ||
    !Number.isSafeInteger(value.expiresAt) ||
    value.expiresAt < 0
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
    roleIds: Object.freeze([...value.roleIds]),
    expiresAt: value.expiresAt,
  });
}

function normalizeUpdateResponse(value, request) {
  requireExactKeys(
    value,
    ["schemaVersion", "orgId", "outcome", "revision"],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    normalizeOrgId(value.orgId, "invalid-response") !== request.orgId ||
    !["updated", "unchanged"].includes(value.outcome)
  ) {
    fail("invalid-response");
  }
  const revision = normalizeRevision(value.revision, "invalid-response");
  if (
    (value.outcome === "unchanged" && revision !== request.expectedRevision) ||
    (value.outcome === "updated" && revision <= request.expectedRevision)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    orgId: request.orgId,
    outcome: value.outcome,
    revision,
  });
}

function normalizeJoinResponse(value, request) {
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "orgId",
      "outcome",
      "membershipStatus",
      "membershipRevision",
    ],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    normalizeOrgId(value.orgId, "invalid-response") !== request.orgId ||
    value.outcome !== "joined" ||
    value.membershipStatus !== "active"
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    orgId: request.orgId,
    outcome: "joined",
    membershipStatus: "active",
    membershipRevision: normalizeRevision(
      value.membershipRevision,
      "invalid-response"
    ),
  });
}

function normalizeOrganisationRevisionResponse(value, request, outcomes) {
  requireExactKeys(
    value,
    ["schemaVersion", "orgId", "outcome", "revision"],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    normalizeOrgId(value.orgId, "invalid-response") !== request.orgId ||
    !outcomes.includes(value.outcome)
  ) {
    fail("invalid-response");
  }
  const revision = normalizeRevision(value.revision, "invalid-response");
  if (revision <= request.expectedRevision) fail("invalid-response");
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    orgId: request.orgId,
    outcome: value.outcome,
    revision,
  });
}

function normalizeRoleResponse(value, request, outcomes) {
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "roleId",
      "publicIdentifier",
      "outcome",
      "revision",
      "disclosureEpoch",
    ],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !outcomes.includes(value.outcome) ||
    !Number.isSafeInteger(value.disclosureEpoch) ||
    value.disclosureEpoch < 0
  ) {
    fail("invalid-response");
  }
  const roleId = normalizeOrgId(value.roleId, "invalid-response");
  if (request.roleId && roleId !== request.roleId) fail("invalid-response");
  const revision = normalizeRevision(value.revision, "invalid-response");
  if (
    (value.outcome === "created" && revision !== 1) ||
    (value.outcome === "unchanged" && revision !== request.expectedRevision) ||
    (!["created", "unchanged"].includes(value.outcome) &&
      revision <= request.expectedRevision)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    roleId,
    publicIdentifier: normalizePublicIdentifier(value.publicIdentifier),
    outcome: value.outcome,
    revision,
    disclosureEpoch: value.disclosureEpoch,
  });
}

function normalizeAssignmentResponse(value, request) {
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "outcome",
      "assignmentRef",
      "membershipRevision",
      "assignmentRevision",
      "requiresAcceptance",
    ],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !["assigned", "unchanged"].includes(value.outcome) ||
    typeof value.requiresAcceptance !== "boolean"
  ) {
    fail("invalid-response");
  }
  const membershipRevision = normalizeRevision(
    value.membershipRevision,
    "invalid-response"
  );
  if (
    (value.outcome === "unchanged" &&
      membershipRevision !== request.expectedRevision) ||
    (value.outcome === "assigned" &&
      membershipRevision <= request.expectedRevision)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    outcome: value.outcome,
    assignmentRef: normalizeOpaqueReference(
      value.assignmentRef,
      "invalid-response"
    ),
    membershipRevision,
    assignmentRevision: normalizeRevision(
      value.assignmentRevision,
      "invalid-response"
    ),
    requiresAcceptance: value.requiresAcceptance,
  });
}

function normalizeRemoveAssignmentResponse(value, request) {
  requireExactKeys(
    value,
    ["schemaVersion", "outcome", "membershipRevision"],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !["removed", "unchanged"].includes(value.outcome)
  ) {
    fail("invalid-response");
  }
  const membershipRevision = normalizeRevision(
    value.membershipRevision,
    "invalid-response"
  );
  if (
    (value.outcome === "unchanged" &&
      membershipRevision !== request.expectedRevision) ||
    (value.outcome === "removed" &&
      membershipRevision <= request.expectedRevision)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    outcome: value.outcome,
    membershipRevision,
  });
}

function normalizeAcceptAssignmentResponse(value, request) {
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "outcome",
      "orgId",
      "assignmentRef",
      "assignmentRevision",
      "membershipRevision",
    ],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !["accepted", "unchanged"].includes(value.outcome) ||
    normalizeOrgId(value.orgId, "invalid-response") !== value.orgId ||
    normalizeOpaqueReference(value.assignmentRef, "invalid-response") !==
      request.assignmentRef
  ) {
    fail("invalid-response");
  }
  const assignmentRevision = normalizeRevision(
    value.assignmentRevision,
    "invalid-response"
  );
  if (
    (value.outcome === "unchanged" &&
      assignmentRevision !== request.expectedRevision) ||
    (value.outcome === "accepted" &&
      assignmentRevision <= request.expectedRevision)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    outcome: value.outcome,
    orgId: value.orgId,
    assignmentRef: request.assignmentRef,
    assignmentRevision,
    membershipRevision: normalizeRevision(
      value.membershipRevision,
      "invalid-response"
    ),
  });
}

function normalizeMemberRemovalResponse(value, request) {
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "orgId",
      "outcome",
      "membershipStatus",
      "membershipRevision",
    ],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.orgId !== request.orgId ||
    value.outcome !== "removed" ||
    value.membershipStatus !== "inactive"
  ) {
    fail("invalid-response");
  }
  const membershipRevision = normalizeRevision(
    value.membershipRevision,
    "invalid-response"
  );
  if (membershipRevision <= request.expectedRevision) fail("invalid-response");
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    orgId: request.orgId,
    outcome: "removed",
    membershipStatus: "inactive",
    membershipRevision,
  });
}

function normalizeInviteResponse(value, request) {
  requireExactKeys(
    value,
    ["schemaVersion", "outcome", "inviteToken", "expiresAt"],
    "invalid-response"
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.outcome !== "created" ||
    typeof value.inviteToken !== "string" ||
    !INVITE_TOKEN_PATTERN.test(value.inviteToken) ||
    value.expiresAt !== request.expiresAt
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    outcome: "created",
    inviteToken: value.inviteToken,
    expiresAt: request.expiresAt,
  });
}

function captureActor(authInstance) {
  const actor = authInstance?.currentUser;
  if (
    !actor ||
    typeof actor.uid !== "string" ||
    actor.uid.length < 1 ||
    actor.uid.length > 128 ||
    CONTROL_CHARACTERS.test(actor.uid)
  ) {
    fail("unauthenticated", "Sign in again to update the organisation.");
  }
  return actor;
}

function assertActorIsCurrent(authInstance, actor) {
  if (
    authInstance?.currentUser !== actor ||
    authInstance.currentUser?.uid !== actor.uid
  ) {
    fail(
      "stale-auth-context",
      "The signed-in account changed during the organisation update."
    );
  }
}

function normalizeServiceError(error) {
  if (error instanceof OrganisationCommandAuthorityServiceError) return error;
  const messages = {
    aborted:
      "This organisation changed. Load the latest version and try again.",
    "deadline-exceeded": "The organisation service did not respond in time.",
    "failed-precondition": "The organisation cannot be updated.",
    "invalid-argument": "The organisation update is invalid.",
    "not-found": "The organisation resource is unavailable.",
    "permission-denied": "You cannot update this organisation.",
    "resource-exhausted": "The organisation limit was reached.",
    "stale-auth-context":
      "The signed-in account changed during the organisation update.",
    "ui-deadline": "The organisation service did not respond in time.",
    unauthenticated: "Sign in again to update the organisation.",
    unavailable: "The organisation service is temporarily unavailable.",
  };
  const candidate = String(error?.code || "unknown")
    .split("/")
    .pop();
  const code = Object.hasOwn(messages, candidate) ? candidate : "unknown";
  return new OrganisationCommandAuthorityServiceError(
    code,
    messages[code] || "The organisation could not be updated."
  );
}

export function createOrganisationCommandOperationId(
  prefix = "organisation",
  cryptoProvider = globalThis.crypto
) {
  if (!/^[A-Za-z][A-Za-z0-9_-]{2,24}$/.test(prefix)) {
    fail("invalid-argument", "The organisation operation is invalid.");
  }
  if (typeof cryptoProvider?.randomUUID === "function") {
    const candidate = `${prefix}_${cryptoProvider
      .randomUUID()
      .replaceAll("-", "_")}`;
    if (OPERATION_ID_PATTERN.test(candidate)) return candidate;
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    const candidate = `${prefix}_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("")}`;
    if (OPERATION_ID_PATTERN.test(candidate)) return candidate;
  }
  fail(
    "failed-precondition",
    "Secure organisation operations are unavailable."
  );
}

export function createOrganisationCommandAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  enabled = isOrganisationAuthorityCommandsFeatureEnabled,
} = {}) {
  const callables = new Map();
  const callableFor = (name) => {
    if (!callables.has(name)) {
      callables.set(name, callableFactory(functionsInstance, name));
    }
    return callables.get(name);
  };

  const invoke = async ({ name, request, phase }) => {
    const actor = captureActor(authInstance);
    try {
      const response = await withUiDeadline(callableFor(name)(request), {
        phase,
        timeoutMs: 30_000,
      });
      assertActorIsCurrent(authInstance, actor);
      return response?.data;
    } catch (error) {
      try {
        assertActorIsCurrent(authInstance, actor);
      } catch (authError) {
        throw authError;
      }
      throw normalizeServiceError(error);
    }
  };

  return Object.freeze({
    async joinOrganisation(intentValue) {
      if (!enabled)
        fail("feature-disabled", "Organisation joins are not enabled.");
      const request = normalizeJoinOrganisationIntent(intentValue);
      const value = await invoke({
        name: "joinOrganisation",
        request,
        phase: "joining organisation",
      });
      return normalizeJoinResponse(value, request);
    },

    async updateOrganisation(intentValue) {
      if (!enabled)
        fail("feature-disabled", "Organisation updates are not enabled.");
      const request = normalizeUpdateOrganisationIntent(intentValue);
      const value = await invoke({
        name: "updateOrganisation",
        request,
        phase: "updating organisation",
      });
      return normalizeUpdateResponse(value, request);
    },

    async closeOrganisation(intentValue) {
      if (!enabled)
        fail("feature-disabled", "Organisation closure is not enabled.");
      const request = normalizeOrganisationRevisionIntent(intentValue);
      const value = await invoke({
        name: "closeOrganisation",
        request,
        phase: "closing organisation",
      });
      return normalizeOrganisationRevisionResponse(value, request, ["closed"]);
    },

    async createRole(intentValue) {
      if (!enabled) fail("feature-disabled", "Role creation is not enabled.");
      const request = normalizeCreateRoleIntent(intentValue);
      const value = await invoke({
        name: "createRole",
        request,
        phase: "creating role",
      });
      return normalizeRoleResponse(value, request, ["created"]);
    },

    async updateRole(intentValue) {
      if (!enabled) fail("feature-disabled", "Role updates are not enabled.");
      const request = normalizeUpdateRoleIntent(intentValue);
      const value = await invoke({
        name: "updateRole",
        request,
        phase: "updating role",
      });
      return normalizeRoleResponse(value, request, ["updated", "unchanged"]);
    },

    async deleteRole(intentValue) {
      if (!enabled) fail("feature-disabled", "Role deletion is not enabled.");
      const request = normalizeRoleRevisionIntent(intentValue);
      const value = await invoke({
        name: "deleteRole",
        request,
        phase: "deleting role",
      });
      return normalizeRoleResponse(value, request, ["deleted"]);
    },

    async assignRole(intentValue) {
      if (!enabled) fail("feature-disabled", "Role assignment is not enabled.");
      const request = normalizeMembershipAssignmentIntent(intentValue);
      const value = await invoke({
        name: "assignRole",
        request,
        phase: "assigning role",
      });
      return normalizeAssignmentResponse(value, request);
    },

    async removeRoleAssignment(intentValue) {
      if (!enabled)
        fail("feature-disabled", "Role assignment removal is not enabled.");
      const request = normalizeMembershipAssignmentIntent(intentValue);
      const value = await invoke({
        name: "removeRoleAssignment",
        request,
        phase: "removing role assignment",
      });
      return normalizeRemoveAssignmentResponse(value, request);
    },

    async acceptRoleAssignment(intentValue) {
      if (!enabled)
        fail("feature-disabled", "Role assignment acceptance is not enabled.");
      const request = normalizeAcceptRoleAssignmentIntent(intentValue);
      const value = await invoke({
        name: "acceptRoleAssignment",
        request,
        phase: "accepting role assignment",
      });
      return normalizeAcceptAssignmentResponse(value, request);
    },

    async removeMember(intentValue) {
      if (!enabled)
        fail("feature-disabled", "Membership removal is not enabled.");
      const request = normalizeMembershipRevisionIntent(intentValue);
      const value = await invoke({
        name: "removeMember",
        request,
        phase: "removing member",
      });
      return normalizeMemberRemovalResponse(value, request);
    },

    async createInvite(intentValue) {
      if (!enabled) fail("feature-disabled", "Invite creation is not enabled.");
      const request = normalizeCreateInviteIntent(intentValue);
      const value = await invoke({
        name: "createInvite",
        request,
        phase: "creating invite",
      });
      return normalizeInviteResponse(value, request);
    },
  });
}

export const organisationCommandAuthorityService =
  createOrganisationCommandAuthorityService();
